import { useContext, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigation } from "../../../utils/navigation";
import { AuthUserContext } from "../../../context/AuthUserContext";
import { useTranslation } from "react-i18next";
import { getCategoryImage } from "../../../utils/categoryImages";

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  width: 100%;
  background-color: var(--bg-200);
  overflow-y: auto;
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
`;
const GridContainer = styled.div`
  min-width: 100%;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  position: relative;
  padding: var(--spacing-md) 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

/* --- Nav card --- */
const NavCard = styled.div`
  margin: 0 var(--spacing-md);
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large);
  overflow: hidden;
`;
const NavRow = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 14px var(--spacing-md);
  background: none;
  border: none;
  border-bottom: 1px solid var(--bg-200);
  color: var(--text-100);
  text-align: left;
  transition: background-color 0.15s ease-in-out;

  &:last-of-type {
    border-bottom: none;
  }
  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;
const NavLabel = styled.span`
  flex: 1;
  font-size: var(--font-size-base);
  font-weight: 500;
`;
const Chevron = styled.svg`
  transition: transform 0.25s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0)")};
  flex-shrink: 0;
`;

/* --- Quick brand pills (inside the "Top Brands" row) --- */
const QuickBrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xs);
  padding: 0 var(--spacing-md) var(--spacing-md);
  background-color: var(--bg-100);
  border-bottom: 1px solid var(--bg-200);
`;
const QuickBrandPill = styled.div`
  border-radius: var(--border-radius-large);
  background-color: var(--accent-200);
  color: var(--bg-100);
  padding: 8px 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: opacity 0.15s ease-in-out;

  &:hover,
  &:active {
    opacity: 0.85;
  }
`;

/* --- Brands accordion (full category grid) --- */
const AccordionPanel = styled.div`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "3000px" : "0px")};
  transition: max-height 0.35s ease;
  background-color: var(--bg-200);
`;
const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
`;
const BrandTile = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--spacing-sm) 4px;
  text-decoration: none;
  color: var(--text-100);
  background-color: var(--bg-100);
  border-radius: var(--border-radius-base);
  animation: ${slideDown} 0.2s ease both;
  text-align: center;

  &:hover,
  &:active {
    background-color: var(--bg-300);
  }
`;
const BrandIcon = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background-color: ${({ color }) => color || "var(--bg-200)"};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const BrandIconImg = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
`;
const BrandName = styled.span`
  font-size: var(--font-size-small);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/* --- Account card --- */
const AccountCard = styled.div`
  margin: 0 var(--spacing-md);
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-large);
  background-color: var(--accent-200);
  overflow: hidden;
`;
const AccountGreeting = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--bg-100);
  padding: var(--spacing-md);
`;
const AccountAvatar = styled.div`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background-color: var(--bg-100);
  color: var(--accent-200);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: var(--font-size-large);
  text-transform: uppercase;
`;
const AccountNameBlock = styled.div`
  min-width: 0;
`;
const AccountName = styled.div`
  font-weight: 600;
  font-size: var(--font-size-base);
  text-transform: capitalize;
`;
const AccountEmail = styled.div`
  font-size: var(--font-size-small);
  color: rgba(255, 255, 255, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const AccountLinks = styled.div`
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large) var(--border-radius-large) 0 0;
  padding: 4px var(--spacing-md) 0;
`;
const AccountLink = styled.button`
  text-decoration: none;
  color: var(--text-100);
  background-color: transparent;
  border: none;
  padding: 14px 0;
  display: flex;
  align-items: center;
  width: 100%;
  font-size: var(--font-size-base);
  text-align: left;
  border-bottom: 1px solid var(--bg-200);
  transition: background-color 0.15s ease-in-out;

  &:last-of-type {
    border-bottom: none;
  }
  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;
const AccountLinkLabel = styled.span`
  flex: 1;
`;
const LogoutRow = styled(AccountLink)`
  color: var(--error-color);
  border-bottom: none;
`;

/* --- Guest CTA --- */
const GuestCard = styled.div`
  margin: 0 var(--spacing-md);
  padding: var(--spacing-lg) var(--spacing-md);
  border-radius: var(--border-radius-large);
  background-color: var(--bg-100);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;
const GuestTitle = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: var(--font-size-base);
  color: var(--text-100);
`;
const GuestSubtitle = styled.p`
  margin: 0 0 var(--spacing-sm);
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const ButtonShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;
const TButton = styled.button`
  margin-top: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
`;
const LButton = styled(TButton)`
  background-color: var(--bg-200);
  border: none;
  color: var(--text-100);

  &:hover,
  &:active,
  &:focus {
    background-color: var(--bg-300);
  }
`;

const Logo = styled.div`
  color: var(--text-200);
  font-size: var(--font-size-large);
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.438px;
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  cursor: pointer;
  justify-content: center;
  margin-top: 4px;
`;

const ConfirmContainer = styled.div`
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 999999;
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: var(--bg-200);
  color: var(--text-200);
  position: relative;
  padding: 40px 24px;
  width: 90%;
  border-radius: var(--border-radius-large);
  text-align: center;
  @media (min-width: 768px) {
    width: 400px;
  }
`;
const ConfirmButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
`;
const ConfirmButtonCancel = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  color: var(--text-200);
  background-color: var(--bg-100);
  transition: background-color 0.15s ease-in-out;

  &:hover,
  &:active,
  &:focus {
    background-color: var(--bg-300);
  }
`;
const ConfirmButtonLogout = styled(ConfirmButtonCancel)`
  color: var(--error-color);
  border-color: var(--error-color);

  &:hover,
  &:active,
  &:focus {
    background-color: var(--error-color);
    color: var(--bg-100);
  }
`;

const HamburgerMenuData = ({ setIsOpen }) => {
  const { t } = useTranslation();
  const { categories } = useContext(ProductContext);
  const { authTokens, userProfile, logoutUser } = useContext(AuthUserContext);
  const navigate = useNavigate();
  const {
    goToCategory,
    goToHome,
    goToShop,
    goToBestSeller,
    goToNewArrivals,
    goToFeatured,
    goToLogin,
    goToRegister,
    goToProfile,
    goToOrderHistory,
    goToAddressBook,
  } = useNavigation();

  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const toggleMenu = () => {
    setIsOpen(false);
  };
  const toggleCategory = (event, category) => {
    event.preventDefault();
    navigate(goToCategory(category.name));
    toggleMenu();
  };

  return (
    <Container>
      <Wrapper>
        <GridContainer>
          <NavCard>
            <NavRow
              onClick={() => {
                navigate(goToShop());
                toggleMenu();
              }}
            >
              <NavLabel>{t("HEADER.LINKS.TOP_BRANDS")}</NavLabel>
            </NavRow>
            <QuickBrandGrid>
              {categories.slice(0, 4).map((category, index) => (
                <QuickBrandPill
                  key={index}
                  onClick={(event) => toggleCategory(event, category)}
                >
                  {category.name}
                </QuickBrandPill>
              ))}
            </QuickBrandGrid>

            <NavRow
              onClick={() => {
                navigate(goToFeatured());
                toggleMenu();
              }}
            >
              <NavLabel>{t("HEADER.LINKS.NEW_YEAR_NEW_ME")}</NavLabel>
            </NavRow>

            <NavRow
              onClick={() => {
                navigate(goToShop());
                toggleMenu();
              }}
            >
              <NavLabel>{t("MENU.NICOTINE_POUCHES")}</NavLabel>
            </NavRow>

            <NavRow onClick={() => setIsBrandsOpen((prev) => !prev)}>
              <NavLabel>{t("MENU.BRANDS")}</NavLabel>
              <Chevron
                $isOpen={isBrandsOpen}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="var(--text-200)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Chevron>
            </NavRow>
            <AccordionPanel $isOpen={isBrandsOpen}>
              <BrandGrid>
                {categories &&
                  categories.length > 0 &&
                  categories.slice(0, 100).map((category, index) => (
                    <BrandTile
                      key={index}
                      to="#"
                      onClick={(event) => toggleCategory(event, category)}
                    >
                      <BrandIcon color={category.color}>
                        {getCategoryImage(category.slug) && (
                          <BrandIconImg
                            src={getCategoryImage(category.slug)}
                            alt=""
                            aria-hidden="true"
                          />
                        )}
                      </BrandIcon>
                      <BrandName>{category.name}</BrandName>
                    </BrandTile>
                  ))}
              </BrandGrid>
            </AccordionPanel>

            <NavRow onClick={() => navigate(goToBestSeller())}>
              <NavLabel>{t("MENU.BEST_SELLERS")}</NavLabel>
            </NavRow>
            <NavRow onClick={() => navigate(goToNewArrivals())}>
              <NavLabel>{t("MENU.NEW_ARRIVALS")}</NavLabel>
            </NavRow>
          </NavCard>

          {authTokens ? (
            <AccountCard>
              <AccountGreeting>
                <AccountAvatar>
                  {(userProfile?.first_name || "").charAt(0)}
                </AccountAvatar>
                <AccountNameBlock>
                  <AccountName>
                    {userProfile?.first_name || t("MENU.ACCOUNT")}
                  </AccountName>
                  {userProfile?.email && (
                    <AccountEmail>{userProfile.email}</AccountEmail>
                  )}
                </AccountNameBlock>
              </AccountGreeting>
              <AccountLinks>
                <AccountLink
                  onClick={() => {
                    navigate(goToProfile());
                    toggleMenu();
                  }}
                >
                  <AccountLinkLabel>
                    {t("PROFILE.PROFILE_INFO")}
                  </AccountLinkLabel>
                </AccountLink>
                <AccountLink
                  onClick={() => {
                    navigate(goToOrderHistory());
                    toggleMenu();
                  }}
                >
                  <AccountLinkLabel>{t("PROFILE.ORDERS")}</AccountLinkLabel>
                </AccountLink>
                <AccountLink
                  onClick={() => {
                    navigate(goToAddressBook());
                    toggleMenu();
                  }}
                >
                  <AccountLinkLabel>
                    {t("PROFILE.MANAGE_ADDRESSES")}
                  </AccountLinkLabel>
                </AccountLink>
                <LogoutRow onClick={() => setShowLogoutConfirm(true)}>
                  <AccountLinkLabel>{t("PROFILE.LOGOUT")}</AccountLinkLabel>
                </LogoutRow>
              </AccountLinks>
            </AccountCard>
          ) : (
            <GuestCard>
              <GuestTitle>{t("LOGIN.TITLE")}</GuestTitle>
              <GuestSubtitle>{t("LOGIN.SUBTITLE")}</GuestSubtitle>
              <ButtonShell>
                <TButton
                  onClick={() => {
                    navigate(goToLogin());
                    toggleMenu();
                  }}
                >
                  {t("MENU.LOGIN")}
                </TButton>
                <LButton
                  onClick={() => {
                    navigate(goToRegister());
                    toggleMenu();
                  }}
                >
                  {t("MENU.SIGN_UP")}
                </LButton>
              </ButtonShell>
            </GuestCard>
          )}

          <Logo
            onClick={() => {
              navigate(goToHome());
              toggleMenu();
            }}
            role="button"
            tabIndex={0}
            aria-label={t("ARIA.HOME")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(goToHome());
                toggleMenu();
              }
            }}
          >
            <svg
              width="34"
              height="34"
              viewBox="0 0 298 298"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M148.707 0C66.7245 0 0 66.7245 0 148.707C0 230.689 66.7245 297.414 148.707 297.414C230.689 297.414 297.414 230.689 297.414 148.707C297.414 66.7245 230.689 0 148.707 0ZM294.707 148.707C294.707 188.275 278.843 224.204 253.18 250.567L244.688 242.074C268.158 217.904 282.669 184.962 282.669 148.707C282.669 112.452 267.878 78.8562 243.988 54.6394L252.48 46.1472C278.563 72.557 294.661 108.766 294.661 148.707H294.707ZM17.3576 148.707C17.3576 76.2899 76.2432 17.4043 148.66 17.4043C221.077 17.4043 279.963 76.2899 279.963 148.707C279.963 221.124 221.077 280.009 148.66 280.009C76.2432 280.009 17.3576 221.124 17.3576 148.707ZM250.52 44.2808L242.028 52.7731C217.858 29.2562 184.915 14.7447 148.614 14.7447C112.312 14.7447 79.416 29.2562 55.2458 52.7731L46.7536 44.2808C73.0701 18.6176 108.999 2.75295 148.567 2.75295C188.135 2.75295 224.11 18.6175 250.427 44.3275L250.52 44.2808ZM44.7939 46.1939L53.2862 54.6862C29.3494 78.9029 14.558 112.172 14.558 148.8C14.558 185.429 29.0693 218.044 52.5862 242.214L44.1406 250.707C18.4307 224.39 2.56627 188.415 2.56627 148.847C2.56627 109.279 18.7107 72.6504 44.7939 46.2872V46.1939ZM46.0539 252.667L54.5461 244.174C78.7629 268.064 111.985 282.856 148.614 282.856C185.242 282.856 218.511 268.064 242.728 244.128L251.22 252.62C224.857 278.703 188.602 294.848 148.66 294.848C108.719 294.848 72.5102 278.703 46.147 252.667"
                fill="var(--text-200)"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M207.722 57.7351C202.622 66.9627 200.922 78.8759 205.416 87.3302C214.54 104.491 230.158 114.314 239.894 131.851C273.15 191.781 209.011 201.72 174.784 209.915C155.428 214.537 143.812 230.506 158.492 258.688C214.436 253.98 258.39 207.07 258.39 149.903C258.39 111.144 238.177 77.1018 207.722 57.7266"
                fill="var(--text-200)"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M191.927 105.506C177.373 95.2564 162.525 82.7355 160.972 63.8935C160.307 55.8558 164.875 48.4915 171.632 43.0459C157.459 40.0907 142.84 39.9776 128.623 42.713C114.406 45.4483 100.873 50.9779 88.8086 58.9812C96.9207 91.2874 127.561 94.6763 155.629 103.143C195.722 115.235 214.322 153.656 158.871 159.818C81.4401 168.423 72.2954 187.545 127.861 256.987C133.447 258.095 139.112 258.763 144.803 258.984C114.222 212.039 131.18 198.9 188.95 180.965C245.166 163.485 224.646 128.565 191.925 105.51"
                fill="var(--text-100)"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M123.936 118.509C100.162 114.213 67.312 112.348 58.2119 97.5595C57.3549 96.166 56.5939 94.7157 55.9342 93.2188C45.5264 110.296 40.0342 129.915 40.0626 149.915C40.0626 197.952 71.1131 238.761 114.236 253.341C95.0693 233.567 63.4812 210.21 71.1047 172.043C76.204 146.547 105.038 150.269 127.675 148.404C160.298 145.722 156.82 124.463 123.927 118.509"
                fill="var(--text-200)"
              />
            </svg>
            SnusCo
          </Logo>
        </GridContainer>
      </Wrapper>
      {showLogoutConfirm && (
        <ConfirmContainer>
          <ConfirmWrapper>
            {t("PROFILE.CONFIRM_LOGOUT")}
            <ConfirmButtonRow>
              <ConfirmButtonLogout
                className="button-transparent"
                onClick={() => {
                  logoutUser();
                  setShowLogoutConfirm(false);
                  toggleMenu();
                }}
              >
                {t("BUTTONS.LOGOUT")}
              </ConfirmButtonLogout>
              <ConfirmButtonCancel
                className="button-transparent"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t("BUTTONS.CANCEL")}
              </ConfirmButtonCancel>
            </ConfirmButtonRow>
          </ConfirmWrapper>
        </ConfirmContainer>
      )}
    </Container>
  );
};

export default HamburgerMenuData;
